import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { settingsAPI, shopsAPI } from "../services/api";
import { useMembership } from "../context/MembershipContext";
import { useOnboarding } from "../context/OnboardingContext";
import { LANGUAGES, useI18n } from "../i18n";
import CatalogPicker from "./CatalogPicker";
import CopyFromShop from "./CopyFromShop";
import ModulesChooser from "./ModulesChooser";
import LanguageSelect from "./LanguageSelect";

const STEPS = ["details", "modules", "products", "ready"];
const CURRENCIES = ["TND", "EUR", "USD", "DZD", "MAD", "LYD"];

const inputClass = "mt-1 w-full rounded-lg border border-gray-300 px-4 py-2 text-base focus:ring-2 focus:ring-primary-500";

/**
 * A short first run for a shop that signed itself up: its details, what it
 * uses, its products, and a nudge toward the first sale. Skippable at every
 * step and resumable — where the owner got to is kept on the shop, not on the
 * device, so a different phone picks up where this one stopped.
 *
 * Nothing here is required: every step has a way past it, and everything can be
 * done later from its own screen (the "Get started" card keeps the list).
 */
const OnboardingWizard = () => {
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const { shop, isOwner, setShop, flag } = useMembership();
  const { state, update, refresh } = useOnboarding();
  const [step, setStep] = useState(null);
  const [form, setForm] = useState(null);
  const [logo, setLogo] = useState(null);
  const [shopLanguage, setShopLanguage] = useState(lang);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [picking, setPicking] = useState(false);
  const [copySources, setCopySources] = useState([]);
  const [copying, setCopying] = useState(false);

  const active = Boolean(isOwner && state?.wizard?.applies && shop);
  const multiShop = flag("multi_shop");

  // An owner opening another shop can bring the products of the one they have
  useEffect(() => {
    if (!active || !multiShop) return;
    shopsAPI
      .copySources()
      .then(({ data }) => setCopySources((Array.isArray(data) ? data : []).filter((source) => source.product_count > 0)))
      .catch(() => setCopySources([]));
  }, [active, multiShop]);

  // Start where the owner left off, once the state arrives
  useEffect(() => {
    if (active && step === null) setStep(Math.min(state.wizard.step || 0, STEPS.length - 1));
  }, [active, step, state]);

  useEffect(() => {
    if (shop && form === null) {
      setForm({
        name: shop.name || "",
        phone: shop.phone || "",
        address: shop.address || "",
        currency: shop.currency || "TND",
      });
    }
  }, [shop, form]);

  // The wizard's own count of products, drafts included, as the server sees them
  const productCount = state?.counts?.products || 0;

  if (!active || step === null || !form) return null;

  const go = async (next) => {
    setStep(next);
    setError("");
    try {
      await update({ wizard_step: next });
    } catch {
      /* resuming is a convenience */
    }
  };

  const finish = async (status, destination) => {
    try {
      await update({ wizard: status });
    } catch {
      /* it will simply ask again */
    }
    if (destination) navigate(destination);
  };

  const saveDetails = async () => {
    setBusy(true);
    setError("");

    try {
      const payload = { ...form };
      let response;
      if (logo) {
        const body = new FormData();
        Object.entries(payload).forEach(([key, value]) => body.append(key, value));
        body.append("logo", logo);
        response = await shopsAPI.update(body);
      } else {
        response = await shopsAPI.update(payload);
      }

      if (response.data?.shop) setShop(response.data.shop);

      // The shop's own language, and this device's to match
      await settingsAPI.set("default_language", shopLanguage);
      setLang(shopLanguage);

      await go(1);
    } catch (err) {
      setError(err.response?.data?.message || t("onboarding.wizard.saveFailed"));
    } finally {
      setBusy(false);
    }
  };

  const field = (key, label, props = {}) => (
    <label className="block">
      <span className="text-sm font-medium text-gray-700">{label}</span>
      <input
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className={inputClass}
        {...props}
      />
    </label>
  );

  const body = (() => {
    if (STEPS[step] === "details") {
      return (
        <div className="space-y-4">
          <p className="text-gray-600">{t("onboarding.wizard.details.intro")}</p>
          {field("name", t("onboarding.wizard.details.name"), { required: true, maxLength: 255 })}
          {field("phone", t("onboarding.wizard.details.phone"), { type: "tel", dir: "ltr" })}
          {field("address", t("onboarding.wizard.details.address"))}

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">{t("onboarding.wizard.details.currency")}</span>
              <select
                value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value })}
                className={inputClass}
              >
                {[...new Set([form.currency, ...CURRENCIES])].map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-700">{t("onboarding.wizard.details.language")}</span>
              <select value={shopLanguage} onChange={(e) => setShopLanguage(e.target.value)} className={inputClass}>
                {LANGUAGES.map((language) => (
                  <option key={language.code} value={language.code}>
                    {language.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="block">
            <span className="text-sm font-medium text-gray-700">{t("onboarding.wizard.details.logo")}</span>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setLogo(e.target.files?.[0] || null)}
              className="mt-1 block w-full text-sm text-gray-600 file:me-3 file:rounded-lg file:border-0 file:bg-primary-50 file:px-4 file:py-2 file:font-semibold file:text-primary-700"
            />
          </label>
        </div>
      );
    }

    if (STEPS[step] === "modules") {
      return (
        <div className="space-y-4">
          <p className="text-gray-600">{t("onboarding.wizard.modules.intro")}</p>
          <ModulesChooser compact />
        </div>
      );
    }

    if (STEPS[step] === "products") {
      return (
        <div className="space-y-5 text-center">
          <p className="text-gray-600">{t("onboarding.wizard.products.intro")}</p>
          <div className="text-5xl" aria-hidden="true">
            📚
          </div>
          {productCount > 0 && (
            <p className="font-semibold text-green-700">
              {t("onboarding.wizard.products.count", { count: productCount })}
            </p>
          )}
          {copySources.length > 0 && (
            <button
              type="button"
              onClick={() => setCopying(true)}
              className="w-full rounded-lg bg-primary-600 py-3 text-lg font-semibold text-white"
            >
              🏪 {t("onboarding.wizard.products.copy", { name: copySources.map((source) => source.name).join(", ") })}
            </button>
          )}
          <button
            type="button"
            onClick={() => setPicking(true)}
            className={`w-full rounded-lg py-3 text-lg font-semibold ${
              copySources.length > 0 ? "border border-primary-600 text-primary-700" : "bg-primary-600 text-white"
            }`}
          >
            {t("onboarding.wizard.products.pick")}
          </button>
          <button type="button" onClick={() => go(3)} className="text-sm font-semibold text-gray-600 underline">
            {t("onboarding.wizard.products.later")}
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-5 text-center">
        <div className="text-6xl" aria-hidden="true">
          🎉
        </div>
        <p className="text-gray-600">{t("onboarding.wizard.ready.intro")}</p>
        <button
          type="button"
          onClick={() => finish("done", "/")}
          className="w-full rounded-lg bg-emerald-600 py-3 text-lg font-semibold text-white"
        >
          🧾 {t("onboarding.wizard.ready.register")}
        </button>
        <button
          type="button"
          onClick={() => finish("done")}
          className="w-full rounded-lg border border-gray-300 py-3 font-semibold text-gray-700"
        >
          {t("onboarding.wizard.ready.dashboard")}
        </button>
        <p className="text-xs text-gray-500">{t("onboarding.wizard.ready.pinNote")}</p>
      </div>
    );
  })();

  const last = step === STEPS.length - 1;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("onboarding.wizard.title")}
      className="fixed inset-0 z-[55] flex flex-col bg-white"
    >
      <header className="border-b px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
          <div className="min-w-0 basis-full sm:basis-auto">
            <h2 className="text-lg font-bold text-gray-800">{t("onboarding.wizard.title")}</h2>
            <p className="text-xs text-gray-500">
              {t("onboarding.wizard.stepOf", { number: step + 1, total: STEPS.length })}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <LanguageSelect />
            <button
              type="button"
              onClick={() => finish("skipped")}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700"
            >
              {t("onboarding.wizard.skipAll")}
            </button>
          </div>
        </div>
        <div className="mt-3 flex gap-1.5" aria-hidden="true">
          {STEPS.map((key, index) => (
            <span key={key} className={`h-1.5 flex-1 rounded-full ${index <= step ? "bg-primary-600" : "bg-gray-200"}`} />
          ))}
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-4 py-5">
        <div className="mx-auto max-w-xl">
          <h3 className="mb-3 text-xl font-bold text-gray-800">
            {t(`onboarding.wizard.${STEPS[step]}.title`)}
          </h3>
          {error && <p className="mb-3 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
          {body}
        </div>
      </div>

      {!last && (
        <footer className="flex items-center justify-between gap-3 border-t px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
          <button
            type="button"
            onClick={() => go(step - 1)}
            disabled={step === 0 || busy}
            className="rounded-lg px-4 py-3 font-semibold text-gray-600 disabled:invisible"
          >
            {t("onboarding.wizard.back")}
          </button>
          <button
            type="button"
            onClick={() => (STEPS[step] === "details" ? saveDetails() : go(step + 1))}
            disabled={busy || (STEPS[step] === "details" && !form.name.trim())}
            className="rounded-lg bg-primary-600 px-8 py-3 font-semibold text-white disabled:opacity-50"
          >
            {busy ? t("onboarding.wizard.saving") : t("onboarding.wizard.next")}
          </button>
        </footer>
      )}

      {picking && (
        <CatalogPicker
          currency={form.currency}
          skipLabel
          onClose={() => setPicking(false)}
          onDone={() => refresh()}
        />
      )}

      {copying && (
        <CopyFromShop
          sources={copySources}
          currency={form.currency}
          zClass="z-[60]"
          onClose={() => setCopying(false)}
          onDone={() => refresh()}
        />
      )}
    </div>,
    document.body,
  );
};

export default OnboardingWizard;
